import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useNavigate, Link } from "react-router-dom";
import { Clock, DollarSign, Package, Shield, Zap, CheckCircle2, Star, ArrowRight, Users, TrendingUp, Target } from "lucide-react";

const Welcome = () => {
  const navigate = useNavigate();

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Gamification Strategy Generator",
    "description": "AI-powered gamification strategy tool for mobile apps. Get personalized engagement mechanics, implementation guidelines, and design specs in 10 minutes.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "79.00",
      "priceCurrency": "USD",
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "500",
    },
  };

  return (
    <>
      <SEO 
        title="AI-Powered App Gamification Strategy Generator"
        description="Transform your app with personalized gamification. Get an AI-powered strategy with implementation guidelines, technical specs, and design guidelines in 10 minutes for only $79."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/"
        keywords="gamification strategy, app engagement, user retention, AI strategy generator, mobile app gamification, game mechanics, user motivation, app growth"
        jsonLd={homeSchema}
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main>
          {/* Hero Section */}
          <section className="pt-32 pb-20 px-4" aria-labelledby="hero-heading">
            <div className="max-w-7xl mx-auto text-center space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Star className="w-4 h-4 fill-current" />
                <span>Trusted by 500+ App Developers</span>
              </div>
              
              <h1 id="hero-heading" className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight">
                Discover Your Perfect
                <br />
                Gamification Strategy
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Get a personalized, AI-powered gamification strategy for your app in 10 minutes. 
                <strong className="text-foreground"> Increase engagement by up to 145%.</strong>
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Button 
                  size="lg"
                  onClick={() => navigate("/pricing")}
                  className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg"
                >
                  Get Started - $79
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/how-it-works")}
                  className="text-lg px-10 py-6"
                >
                  See How It Works
                </Button>
              </div>

              {/* Trust Signals */}
              <div className="flex flex-col items-center gap-4 pt-8">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-accent fill-current" />
                ))}
                <span className="ml-2 text-muted-foreground">4.8/5 from 500+ reviews</span>
              </div>
                <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>30-Day Money-Back Guarantee</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    <span>Instant Delivery</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>Secure Payment via Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-16 px-4 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5" aria-label="Statistics">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div className="space-y-2">
                  <p className="text-4xl md:text-5xl font-bold text-primary">500+</p>
                  <p className="text-muted-foreground">Strategies Generated</p>
                </div>
                <div className="space-y-2">
                  <p className="text-4xl md:text-5xl font-bold text-primary">145%</p>
                  <p className="text-muted-foreground">Avg. Engagement Increase</p>
                </div>
                <div className="space-y-2">
                  <p className="text-4xl md:text-5xl font-bold text-primary">10 min</p>
                  <p className="text-muted-foreground">Time to Complete</p>
                </div>
                <div className="space-y-2">
                  <p className="text-4xl md:text-5xl font-bold text-primary">$79</p>
                  <p className="text-muted-foreground">One-Time Payment</p>
                </div>
              </div>
            </div>
          </section>

          {/* 3-Column Benefits */}
          <section className="py-20 px-4" aria-labelledby="benefits-heading">
            <div className="max-w-7xl mx-auto">
              <header className="text-center mb-12">
                <h2 id="benefits-heading" className="text-4xl font-bold mb-4">Why Choose Our Gamification Tool?</h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Skip months of research and expensive consultants. Get actionable strategies instantly.
                </p>
              </header>
              
              <div className="grid md:grid-cols-3 gap-8">
                <Card className="p-8 text-center space-y-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-primary/10">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mx-auto">
                    <Clock className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">Save 20+ Hours</h3>
                  <p className="text-muted-foreground">
                    Skip the research. Our AI analyzes your app and delivers a complete strategy in under 15 minutes.
                  </p>
                </Card>

                <Card className="p-8 text-center space-y-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-accent/20">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-accent/20 to-primary/20 mx-auto">
                    <DollarSign className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-2xl font-bold">Save $5,000+</h3>
                  <p className="text-muted-foreground">
                    Only $79 vs. $5,000-$15,000 for traditional gamification consultants.
                  </p>
                </Card>

                <Card className="p-8 text-center space-y-4 shadow-lg hover:shadow-xl transition-shadow border-2 border-primary/10">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mx-auto">
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">Complete Package</h3>
                  <p className="text-muted-foreground">
                    Strategy, technical specs (JSON), design guidelines (PDF), and implementation roadmap included.
                  </p>
                </Card>
              </div>
            </div>
          </section>

          {/* How It Works Preview */}
          <section className="py-20 px-4 bg-gradient-to-b from-background to-primary/5" aria-labelledby="process-heading">
            <div className="max-w-5xl mx-auto">
              <header className="text-center mb-12">
                <h2 id="process-heading" className="text-4xl font-bold mb-4">Simple 3-Step Process</h2>
                <p className="text-xl text-muted-foreground">From assessment to implementation in minutes</p>
              </header>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto">1</div>
                  <h3 className="text-xl font-semibold">Purchase Access</h3>
                  <p className="text-muted-foreground">Secure $79 one-time payment via Stripe</p>
                </div>
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto">2</div>
                  <h3 className="text-xl font-semibold">Complete Assessment</h3>
                  <p className="text-muted-foreground">Answer 7 questions about your app (10 min)</p>
                </div>
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto">3</div>
                  <h3 className="text-xl font-semibold">Get Your Strategy</h3>
                  <p className="text-muted-foreground">Receive personalized recommendations instantly</p>
                </div>
              </div>
              
              <div className="text-center mt-12">
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/how-it-works")}
                  className="text-lg"
                >
                  Learn More About the Process
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </section>

          {/* Use Cases */}
          <section className="py-20 px-4" aria-labelledby="usecases-heading">
            <div className="max-w-6xl mx-auto">
              <header className="text-center mb-12">
                <h2 id="usecases-heading" className="text-4xl font-bold mb-4">Works for Every App Category</h2>
                <p className="text-xl text-muted-foreground">Tailored strategies for your specific industry</p>
              </header>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: Target, title: "Education & Learning", desc: "Motivate students with progress tracking and achievements" },
                  { icon: TrendingUp, title: "Health & Fitness", desc: "Drive healthy habits with challenges and rewards" },
                  { icon: Users, title: "Social & Community", desc: "Boost engagement with social mechanics and badges" },
                  { icon: Zap, title: "Productivity", desc: "Gamify task completion with streaks and milestones" },
                  { icon: Star, title: "Entertainment", desc: "Maximize retention with reward loops and unlocks" },
                  { icon: Package, title: "E-commerce", desc: "Increase conversions with loyalty programs" },
                ].map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{item.title}</h3>
                          <p className="text-sm text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background" aria-labelledby="cta-heading">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold">
                Ready to Transform Your App?
              </h2>
              <p className="text-xl text-muted-foreground">
                Join 500+ developers who have already discovered their perfect gamification strategy
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate("/pricing")}
                  className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg"
                >
                  Get Started - $79
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                30-day money-back guarantee • Instant delivery • Secure payment
              </p>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Welcome;
